import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import SearchBar from "@/components/form/hook-form/search-bar";
import { BackIcon } from "@/components/icons/back-icon";
import { StackedModal } from "@/components/modal/stacked-modal";
import { useGetAlternativePlans } from "@/hooks/requests/plan/use-get-alternative-plans";
import { useGetSearchTags } from "@/hooks/requests/plan/use-get-search-tags";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { getMealTimePageTitle } from "../../utils/get-meal-time-page-title";
import { AlternativeMealsList } from "../alternative-meals-list";
import { SearchActionButton } from "../search-action-button";
import { SearchTags } from "../search-tags";

interface SearchFormValues {
  search: string;
}

interface SwapPlanSearchModalProps {
  pageRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  /** When true, the modal is being reopened and should keep its existing
   * search state so the cached results are shown without refetching. */
  shouldRestore?: boolean;
  onDismiss: () => void;
  onOpenMealDetail?: () => void;
  mealData: Meal | null;
  mealTime: MealTime | undefined;
  mealDay: string | undefined;
  mealIndex: number | undefined;
  targetDate?: string;
  isExtraMeal?: boolean;
}

export const SwapPlanSearchModal = memo(function SwapPlanSearchModal({
  pageRef,
  isOpen,
  shouldRestore = false,
  onDismiss,
  onOpenMealDetail,
  mealData,
  mealTime,
  mealDay,
  mealIndex,
  targetDate,
  isExtraMeal,
}: SwapPlanSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);

  const { control, watch, setValue, reset } = useForm<SearchFormValues>({
    defaultValues: {
      search: "",
    },
  });

  const searchValue = watch("search");
  const { searchTags, isLoading: tagsLoading } = useGetSearchTags(mealTime ?? "");

  const {
    plans,
    requestId,
    isLoading: mealsLoading,
    isFetching,
  } = useGetAlternativePlans(
    isOpen ? (mealData?.id ?? "") : "",
    mealTime ?? MealTime.BREAKFAST,
    mealDay,
    undefined,
    undefined,
    undefined,
    searchValue,
    isExtraMeal,
    targetDate,
  );

  // Extract unique meals from API response
  const alternativeMeals = useMemo(() => {
    const data = plans?.meals?.[0]?.mealTime.data ?? [];
    const idSet = new Set<string>();
    const uniqueMeals = data.filter((meal) => {
      if (idSet.has(meal.id ?? "")) {
        return false;
      }
      idSet.add(meal.id ?? "");
      return true;
    });
    return uniqueMeals;
  }, [plans]);

  const handleTagClick = useCallback(
    (tag: string) => {
      setValue("search", tag);
      setSearchQuery(tag);
      setShowResults(true);
    },
    [setValue],
  );

  const handleSearch = useCallback(() => {
    setSearchQuery(searchValue);
    setShowResults(true);
  }, [searchValue]);

  const handleClearSearch = useCallback(() => {
    reset({ search: "" });
    setSearchQuery("");
    setShowResults(false);
  }, [reset]);

  // Reset to SearchActionButton when search input changes while showing results
  useEffect(() => {
    if (showResults && searchValue !== searchQuery) {
      setShowResults(false);
    }
  }, [searchValue, searchQuery, showResults]);

  // On a fresh open, reset to a clean search. On a reopen (returning from meal
  // detail), keep the existing form/state so the cached results render instantly.
  useEffect(() => {
    if (!isOpen || shouldRestore) {
      return;
    }
    reset();
    setSearchQuery("");
    setShowResults(false);
  }, [isOpen, shouldRestore, reset]);

  if (!mealData || !mealTime || !mealDay) {
    return null;
  }

  return (
    <StackedModal
      pageRef={pageRef}
      isOpen={isOpen}
      onDidDismiss={onDismiss}
      title={getMealTimePageTitle(mealTime)}
      showConfirmButton={false}
      scrollY={false}
      forceOverscroll={false}
      classNames={{
        content: "overflow-hidden bg-white p-0",
      }}
      renderCloseButton={
        searchValue || showResults
          ? (handleClose) => (
              <button
                onClick={() => {
                  if (searchValue || showResults) {
                    handleClearSearch();
                  } else {
                    handleClose();
                  }
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full"
                aria-label="Clear search"
              >
                <BackIcon />
              </button>
            )
          : undefined
      }
    >
      <div className="flex h-full min-h-0 w-full flex-col px-4 pt-2">
        <SearchBar
          control={control}
          name="search"
          placeholder="Search"
          className="w-full"
          aria-label="Search meals"
          enterKeyHint="search"
          onSubmit={handleSearch}
          onClear={handleClearSearch}
        />

        <div className="ion-content-scroll-host no-scrollbar min-h-0 flex-1 gap-3.5 overflow-y-auto overscroll-contain px-0 py-6">
          {searchValue ? (
            showResults ? (
              <AlternativeMealsList
                meals={alternativeMeals}
                mealTime={mealTime}
                mealDay={mealDay}
                mealIndex={mealIndex}
                currentMealId={mealData?.id ?? undefined}
                targetDate={targetDate}
                requestId={requestId}
                isLoading={mealsLoading}
                isFetching={isFetching}
                onClearSearch={handleClearSearch}
                onComplete={onDismiss}
                onOpenMealDetail={onOpenMealDetail}
                isExtra={isExtraMeal}
              />
            ) : (
              <SearchActionButton onSearch={handleSearch} />
            )
          ) : (
            <SearchTags tags={searchTags} isLoading={tagsLoading} onTagClick={handleTagClick} />
          )}
        </div>
      </div>
    </StackedModal>
  );
});
